import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import {
  List, MagnifyingGlass, Warning, ArrowsClockwise, Upload, DownloadSimple,
  Eye, Pencil, ArrowsLeftRight, Cube, Warehouse as WarehouseIcon, Check,
  CaretUp, CaretDown, Package, Funnel
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSellerMode } from '../contexts/SellerModeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STOCK_HEALTH = {
  healthy: { label: 'Healthy', cls: 'bg-green-100 text-green-700' },
  low_stock: { label: 'Low', cls: 'bg-amber-100 text-amber-700' },
  out_of_stock: { label: 'OOS', cls: 'bg-red-100 text-red-700' },
};
const ADJUST_REASONS = ['received','damaged','lost','cycle_count','return','correction','quarantine','release','write_off','other'];

export default function InventoryPage() {
  const { activeSeller } = useSellerMode();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({ warehouse_id: '', stock_status: '', category: '', search: '' });
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selected, setSelected] = useState(new Set());

  // Adjust dialog
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ adjustment_type: 'absolute', quantity: '', reason: 'received', notes: '' });
  const [adjusting, setAdjusting] = useState(false);

  // Transfer dialog
  const [transferDialog, setTransferDialog] = useState(null);
  const [transferForm, setTransferForm] = useState({ to_warehouse_id: '', quantity: '', notes: '' });
  const [transferring, setTransferring] = useState(false);

  // Detail drawer
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/warehouses`, { withCredentials: true }).then(r => setWarehouses(r.data?.warehouses || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchInventory, 200);
    return () => clearTimeout(t);
  }, [page, filters, sortBy, sortOrder, activeSeller?.user_id]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, sort_by: sortBy, sort_order: sortOrder });
      if (filters.warehouse_id) params.set('warehouse_id', filters.warehouse_id);
      if (filters.stock_status) params.set('stock_status', filters.stock_status);
      if (filters.category) params.set('category', filters.category);
      if (filters.search) params.set('search', filters.search);
      const { data } = await axios.get(`${API}/api/inventory/v2?${params}`, { withCredentials: true });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  };

  const openDetail = async (inv) => {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/inventory/v2/${inv.inventory_id}`, { withCredentials: true });
      setDetail(data);
    } catch { setDetail(inv); }
    finally { setDetailLoading(false); }
  };

  const handleAdjust = async () => {
    if (!adjustForm.quantity) { toast.error('Quantity required'); return; }
    setAdjusting(true);
    try {
      await axios.post(`${API}/api/inventory/adjust`, {
        inventory_id: adjustDialog.inventory_id,
        adjustment_type: adjustForm.adjustment_type,
        quantity: parseInt(adjustForm.quantity),
        reason: adjustForm.reason,
        notes: adjustForm.notes,
      }, { withCredentials: true });
      toast.success('Stock adjusted');
      setAdjustDialog(null);
      fetchInventory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAdjusting(false); }
  };

  const handleTransfer = async () => {
    if (!transferForm.quantity || !transferForm.to_warehouse_id) { toast.error('Warehouse and quantity required'); return; }
    setTransferring(true);
    try {
      await axios.post(`${API}/api/inventory/transfer`, {
        product_id: transferDialog.product_id,
        from_warehouse_id: transferDialog.warehouse_id,
        to_warehouse_id: transferForm.to_warehouse_id,
        quantity: parseInt(transferForm.quantity),
        notes: transferForm.notes,
      }, { withCredentials: true });
      toast.success('Stock transferred');
      setTransferDialog(null);
      fetchInventory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setTransferring(false); }
  };

  const handleExport = async () => {
    try {
      const { data } = await axios.get(`${API}/api/inventory/export?warehouse_id=${filters.warehouse_id}`, { withCredentials: true });
      if (!data.rows?.length) { toast.info('No inventory to export'); return; }
      const csv = [Object.keys(data.rows[0]).join(','), ...data.rows.map(r => Object.values(r).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'inventory_export.csv'; a.click();
      toast.success(`Exported ${data.total} rows`);
    } catch { toast.error('Export failed'); }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('desc'); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === items.length ? new Set() : new Set(items.map(i => i.inventory_id)));

  const totalPages = Math.ceil(total / 50);
  const fmt = v => new Intl.NumberFormat('en-IN').format(v || 0);

  const SortHeader = ({ col, children }) => (
    <button onClick={() => toggleSort(col)} className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7] hover:text-[#7d64ff]">
      {children}
      {sortBy === col && (sortOrder === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
    </button>
  );

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="inventory-page-v2">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Inventory Management</h1>
            <p className="text-sm text-[#6b6b8a] mt-0.5">{fmt(stats.total_skus)} SKUs &middot; {fmt(stats.total_quantity)} total units</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-inventory-btn"><DownloadSimple size={16} className="mr-1.5" /> Export</Button>
            <Button onClick={() => navigate('/inventory/bulk-upload')} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="bulk-upload-btn"><Upload size={16} className="mr-1.5" /> Bulk Upload</Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white border border-[#e5e5ea] rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold text-[#9c9db7] uppercase">Total SKUs</p>
            <p className="text-xl font-bold text-[#0A0A0A]">{fmt(stats.total_skus)}</p>
          </div>
          <div className="bg-white border border-[#e5e5ea] rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold text-[#9c9db7] uppercase">Total Units</p>
            <p className="text-xl font-bold text-[#0A0A0A]">{fmt(stats.total_quantity)}</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold text-amber-600 uppercase">Low Stock</p>
            <p className="text-xl font-bold text-amber-700">{fmt(stats.low_stock)}</p>
          </div>
          <div className="bg-white border border-red-200 rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold text-red-500 uppercase">Out of Stock</p>
            <p className="text-xl font-bold text-red-600">{fmt(stats.out_of_stock)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#e5e5ea] rounded-xl mb-4">
          <div className="action-bar">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" />
              <Input value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }} placeholder="Search SKU or product..." className="pl-9 h-9 text-sm" data-testid="inventory-search" />
            </div>
            <Select value={filters.warehouse_id || "all"} onValueChange={v => { setFilters(f => ({ ...f, warehouse_id: v === "all" ? "" : v })); setPage(1); }}>
              <SelectTrigger className="w-44 h-9" data-testid="warehouse-filter"><WarehouseIcon size={14} className="mr-1.5 text-[#9c9db7]" /><SelectValue placeholder="All Warehouses" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Warehouses</SelectItem>{warehouses.map(w => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.stock_status || "all"} onValueChange={v => { setFilters(f => ({ ...f, stock_status: v === "all" ? "" : v })); setPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="stock-status-filter"><Funnel size={14} className="mr-1.5 text-[#9c9db7]" /><SelectValue placeholder="Stock Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Stock</SelectItem><SelectItem value="healthy">Healthy</SelectItem><SelectItem value="low">Low Stock</SelectItem><SelectItem value="out">Out of Stock</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-2">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-12 skeleton rounded-lg" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <List size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No inventory records found</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inventory-table">
                <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea] sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-3"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} className="rounded" /></th>
                    <th className="text-left px-3 py-3"><SortHeader col="product.sku">SKU</SortHeader></th>
                    <th className="text-left px-3 py-3"><SortHeader col="product.name">Product</SortHeader></th>
                    <th className="text-left px-3 py-3"><SortHeader col="warehouse.name">Warehouse</SortHeader></th>
                    <th className="text-right px-3 py-3"><SortHeader col="quantity">Available</SortHeader></th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Reserved</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Blocked</th>
                    <th className="text-left px-3 py-3"><SortHeader col="stock_health">Health</SortHeader></th>
                    <th className="text-left px-3 py-3"><SortHeader col="updated_at">Updated</SortHeader></th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(inv => {
                    const health = STOCK_HEALTH[inv.stock_health] || STOCK_HEALTH.healthy;
                    return (
                      <tr key={inv.inventory_id} className={`border-b border-[#f0f0f0] hover:bg-[#fafafe] ${selected.has(inv.inventory_id) ? 'bg-[#7d64ff]/5' : ''}`}>
                        <td className="px-3 py-2.5"><input type="checkbox" checked={selected.has(inv.inventory_id)} onChange={() => toggleSelect(inv.inventory_id)} className="rounded" /></td>
                        <td className="px-3 py-2.5 font-mono text-xs font-medium text-[#7d64ff]">{inv.product?.sku || inv.product_id}</td>
                        <td className="px-3 py-2.5"><div className="max-w-[200px] truncate text-[#0A0A0A] font-medium text-xs">{inv.product?.name || '—'}</div><div className="text-[10px] text-[#9c9db7]">{inv.product?.category}</div></td>
                        <td className="px-3 py-2.5 text-xs text-[#6b6b8a]">{inv.warehouse?.name || inv.warehouse_id}</td>
                        <td className={`px-3 py-2.5 text-right font-bold ${inv.quantity === 0 ? 'text-red-600' : inv.stock_health === 'low_stock' ? 'text-amber-600' : 'text-[#0A0A0A]'}`}>{fmt(inv.quantity)}</td>
                        <td className="px-3 py-2.5 text-right text-xs text-[#9c9db7]">{inv.reserved_quantity || 0}</td>
                        <td className="px-3 py-2.5 text-right text-xs text-[#9c9db7]">{inv.blocked_quantity || 0}</td>
                        <td className="px-3 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${health.cls}`}>{health.label}</span></td>
                        <td className="px-3 py-2.5 text-[10px] text-[#9c9db7]">{inv.updated_at ? new Date(inv.updated_at).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex gap-0.5 justify-end">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(inv)} data-testid={`view-inv-${inv.inventory_id}`}><Eye size={14} /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setAdjustDialog(inv); setAdjustForm({ adjustment_type: 'absolute', quantity: String(inv.quantity), reason: 'received', notes: '' }); }} data-testid={`adjust-inv-${inv.inventory_id}`}><Pencil size={14} /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#7d64ff]" onClick={() => { setTransferDialog(inv); setTransferForm({ to_warehouse_id: '', quantity: '', notes: '' }); }} data-testid={`transfer-inv-${inv.inventory_id}`}><ArrowsLeftRight size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-[#9c9db7]">Showing {(page-1)*50+1}–{Math.min(page*50, total)} of {fmt(total)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-[#6b6b8a] py-1.5">Page {page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}

        {/* Detail Drawer */}
        <Sheet open={!!detail || detailLoading} onOpenChange={() => setDetail(null)}>
          <SheetContent side="right" className="w-full sm:max-w-[520px] p-0 overflow-y-auto" data-testid="inventory-detail-drawer">
            {detailLoading ? <div className="p-6 space-y-4"><div className="h-8 skeleton rounded-lg" /><div className="h-40 skeleton rounded-xl" /><div className="h-32 skeleton rounded-xl" /></div> : detail && (
              <>
                <div className="sticky top-0 bg-white z-10 border-b border-[#e5e5ea] px-6 py-4">
                  <SheetHeader><SheetTitle>{detail.product?.name || detail.product_id}</SheetTitle></SheetHeader>
                  <p className="text-xs font-mono text-[#9c9db7]">{detail.product?.sku}</p>
                </div>
                <div className="p-6 space-y-5">
                  {/* Stock Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center"><p className="text-[10px] text-green-600 font-semibold uppercase">Available</p><p className="text-lg font-bold text-green-700">{fmt(detail.available_quantity)}</p></div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center"><p className="text-[10px] text-amber-600 font-semibold uppercase">Reserved</p><p className="text-lg font-bold text-amber-700">{fmt(detail.reserved_quantity || 0)}</p></div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center"><p className="text-[10px] text-red-600 font-semibold uppercase">Blocked</p><p className="text-lg font-bold text-red-700">{fmt(detail.blocked_quantity || 0)}</p></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center"><p className="text-[10px] text-blue-600 font-semibold uppercase">Inbound</p><p className="text-lg font-bold text-blue-700">{fmt(detail.inbound_quantity || 0)}</p></div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center"><p className="text-[10px] text-purple-600 font-semibold uppercase">Damaged</p><p className="text-lg font-bold text-purple-700">{fmt(detail.damaged_quantity || 0)}</p></div>
                    <div className="bg-[#f9f9fb] border border-[#e5e5ea] rounded-xl p-3 text-center"><p className="text-[10px] text-[#6b6b8a] font-semibold uppercase">Total</p><p className="text-lg font-bold">{fmt(detail.quantity)}</p></div>
                  </div>

                  {/* Settings */}
                  <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                    <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-3">Thresholds</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between"><span className="text-[#6b6b8a]">Safety Stock</span><span className="font-medium">{detail.safety_stock || 0}</span></div>
                      <div className="flex justify-between"><span className="text-[#6b6b8a]">Reorder At</span><span className="font-medium">{detail.reorder_threshold || 0}</span></div>
                      <div className="flex justify-between"><span className="text-[#6b6b8a]">Low Stock At</span><span className="font-medium">{detail.low_stock_threshold || 10}</span></div>
                      <div className="flex justify-between"><span className="text-[#6b6b8a]">Buffer</span><span className="font-medium">{detail.buffer_stock || 0}</span></div>
                    </div>
                  </div>

                  {/* All Warehouse Stock */}
                  {detail.all_warehouse_stock?.length > 1 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-3">All Warehouses ({detail.all_warehouse_stock.length})</h3>
                      <div className="space-y-1.5">{detail.all_warehouse_stock.map(w => (
                        <div key={w.inventory_id} className="flex justify-between text-sm">
                          <span className="text-[#3c3c64]">{w.warehouse_id}</span>
                          <span className={`font-bold ${w.quantity === 0 ? 'text-red-600' : 'text-[#0A0A0A]'}`}>{fmt(w.quantity)}</span>
                        </div>
                      ))}</div>
                    </div>
                  )}

                  {/* Recent Adjustments */}
                  {detail.recent_adjustments?.length > 0 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-3">Recent Adjustments</h3>
                      <div className="space-y-2">{detail.recent_adjustments.slice(0, 5).map(a => (
                        <div key={a.adjustment_id} className="flex justify-between items-center text-xs">
                          <div><span className={`font-bold ${a.change > 0 ? 'text-green-600' : 'text-red-600'}`}>{a.change > 0 ? '+' : ''}{a.change}</span> <span className="text-[#9c9db7]">{a.reason}</span></div>
                          <span className="text-[#9c9db7]">{new Date(a.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button onClick={() => { setDetail(null); setAdjustDialog(detail); setAdjustForm({ adjustment_type: 'absolute', quantity: String(detail.quantity), reason: 'received', notes: '' }); }} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white"><Pencil size={14} className="mr-1" /> Adjust</Button>
                    <Button variant="outline" onClick={() => { setDetail(null); setTransferDialog(detail); setTransferForm({ to_warehouse_id: '', quantity: '', notes: '' }); }}><ArrowsLeftRight size={14} className="mr-1" /> Transfer</Button>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Adjust Dialog */}
        <Dialog open={!!adjustDialog} onOpenChange={() => setAdjustDialog(null)}>
          <DialogContent className="max-w-sm" data-testid="adjust-dialog">
            <DialogHeader><DialogTitle>Adjust Stock</DialogTitle></DialogHeader>
            {adjustDialog && <p className="text-xs text-[#9c9db7] -mt-2">Current: {adjustDialog.quantity} units</p>}
            <div className="space-y-3">
              <div><Label>Type</Label>
                <Select value={adjustForm.adjustment_type} onValueChange={v => setAdjustForm(f => ({ ...f, adjustment_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="absolute">Set to</SelectItem><SelectItem value="increment">Add</SelectItem><SelectItem value="decrement">Remove</SelectItem></SelectContent></Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" min="0" value={adjustForm.quantity} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))} data-testid="adjust-qty" /></div>
              <div><Label>Reason</Label>
                <Select value={adjustForm.reason} onValueChange={v => setAdjustForm(f => ({ ...f, reason: v }))}><SelectTrigger data-testid="adjust-reason"><SelectValue /></SelectTrigger><SelectContent>{ADJUST_REASONS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Notes</Label><Textarea value={adjustForm.notes} onChange={e => setAdjustForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes..." /></div>
              <Button onClick={handleAdjust} disabled={adjusting} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="confirm-adjust-btn">{adjusting ? 'Adjusting...' : 'Confirm Adjustment'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Transfer Dialog */}
        <Dialog open={!!transferDialog} onOpenChange={() => setTransferDialog(null)}>
          <DialogContent className="max-w-sm" data-testid="transfer-dialog">
            <DialogHeader><DialogTitle>Transfer Stock</DialogTitle></DialogHeader>
            {transferDialog && <p className="text-xs text-[#9c9db7] -mt-2">From: {transferDialog.warehouse?.name || transferDialog.warehouse_id} ({transferDialog.quantity} units)</p>}
            <div className="space-y-3">
              <div><Label>To Warehouse</Label>
                <Select value={transferForm.to_warehouse_id || "none"} onValueChange={v => setTransferForm(f => ({ ...f, to_warehouse_id: v === "none" ? "" : v }))}><SelectTrigger data-testid="transfer-to-wh"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Select warehouse</SelectItem>{warehouses.filter(w => w.warehouse_id !== transferDialog?.warehouse_id).map(w => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" min="1" value={transferForm.quantity} onChange={e => setTransferForm(f => ({ ...f, quantity: e.target.value }))} data-testid="transfer-qty" /></div>
              <div><Label>Notes</Label><Textarea value={transferForm.notes} onChange={e => setTransferForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
              <Button onClick={handleTransfer} disabled={transferring} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="confirm-transfer-btn">{transferring ? 'Transferring...' : 'Confirm Transfer'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
